import type { BlobLike } from "@pdfking/compatibility"
import type {
  PageNumber,
  PDFMergeFunction,
  PDFMergeInput,
} from "@pdfking/renderer-api"

import {
  PdfiumExports as E,
  type PdfiumApi,
  type PdfiumTypeMap,
} from "@pdfking/pdfium-wrap"

import { CloseDocument } from "../functions/CloseDocument"
import { ReadDocument } from "../functions/ReadDocument"
import { SaveDocument } from "../functions/SaveDocument"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

type PointerDoc = PdfiumTypeMap["FPDF_DOCUMENT"]

const findInsertIndex = (arr: PageNumber[], num: PageNumber): number => {
  const l = arr.length
  if (l <= 8) {
    for (let i = 0; i < l; i++) {
      if (arr[i] > num) return i
    }
    return l
  }
  let left = 0
  let right = l - 1
  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    if (arr[mid] <= num) left = mid + 1
    else right = mid - 1
  }
  return left < l ? left : l
}

const CopyPage = async (
  { safeCall, writeString }: PdfiumApi,
  source_doc_pointer: PointerDoc,
  dest_doc_pointer: PointerDoc,
  insert_at: number
): Promise<void> => {
  const { free, pointer: range } = writeString("")

  await safeCall(
    E.FPDF_ImportPages,
    dest_doc_pointer,
    source_doc_pointer,
    range,
    insert_at - 1
  )

  free()
}

async function* PDFMergeImpl(
  api: PdfiumApi,
  pages: AsyncIterable<PDFMergeInput> | Iterable<PDFMergeInput>
): AsyncGenerator<BlobLike> {
  const doc_pointer = await api.safeCall(E.FPDF_CreateNewDocument)

  const close = async () => {
    if (doc_pointer) {
      await api.call(E.FPDF_CloseDocument, doc_pointer)
    }
  }

  try {
    const pageRegistry: PageNumber[] = []

    for await (const input of pages) {
      const reader = await ReadDocument(api, input)

      try {
        const pageCount = await api.call(
          E.FPDF_GetPageCount,
          reader.doc_pointer
        )

        if (pageCount !== 1) {
          throw new Error(`Expected source to have 1 page, got ${pageCount}`)
        }

        const insertIndex = findInsertIndex(pageRegistry, input.page_number)
        await CopyPage(api, reader.doc_pointer, doc_pointer, insertIndex + 1)
        pageRegistry.splice(insertIndex, 0, input.page_number)
      } finally {
        await CloseDocument(api, reader)
      }
    }

    yield* SaveDocument(api, doc_pointer)
  } finally {
    await close()
  }
}

export const PDFMergeLegacy: PDFMergeFunction = pages =>
  ProvisionPdfium(api => PDFMergeImpl(api, pages))
